import React, { useEffect, useState, useCallback } from "react";
import {
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const FeeDiscountApprovalReport = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Branch
  const [isMaster, setIsMaster] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(1);

  // Dropdown data
  const [ddlClass, setDdlClass] = useState([]);
  const [ddlSection, setDdlSection] = useState([]);
  const [ddlStudent, setDdlStudent] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Filter state
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [requestedBy, setRequestedBy] = useState("");
  const [approvedBy, setApprovedBy] = useState("");
  const [approvalStatus, setApprovalStatus] = useState(0);
  const [executionStatus, setExecutionStatus] = useState(0);

  // Table & loading
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0];
  };
  const getCurrentDate = () => new Date().toISOString().split("T")[0];

  useEffect(() => {
    setFromDate(getFirstDayOfMonth());
    setToDate(getCurrentDate());
  }, []);

  // ── Branch ──
  const fetchBranches = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        setBranches(await response.json());
      } else {
        console.error("Failed to fetch branches");
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  useEffect(() => {
    const isMasterUser = sessionStorage.getItem("is_Master") === "true";
    setIsMaster(isMasterUser);

    if (isMasterUser) {
      fetchBranches();
    } else {
      const branchId = sessionStorage.getItem("branchId");
      setSelectedBranchId(branchId);
    }
  }, []);

  // ── Dropdown fetches ──
  const fetchDdlClass = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`Error: ${res.status}`);
      const data = await res.json();
      if (data.data === null && data.msg === "Record Not Found") return;
      setDdlClass(data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchDdlSection = async (classId) => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/Exam/ddlSection_clsId`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ classId, teacherId: 0 }),
      });
      if (!res.ok) throw new Error(`Error: ${res.status}`);
      const data = await res.json();
      setDdlSection(data?.data === null ? [] : data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchDdlStudent = async (classId, sectionId) => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/FeeReport/ddlStudentbyClassId`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ classId, sectionId }),
      });
      if (!res.ok) throw new Error(`Error: ${res.status}`);
      const data = await res.json();
      setDdlStudent(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    }
  };

  // Reused from AddUser — employee list for Requested By / Approved By
  const fetchEmployees = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
      const response = await fetch(`${apiUrl}/Employee/GetEmployee?Branch_Id=${branchId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      const responseData = await response.json();
      if (responseData.data === null && responseData.msg === "Record Not Found") {
        setEmployees([]);
        return;
      }
      setEmployees(responseData || []);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  useEffect(() => {
    fetchDdlClass();
  }, []);

  useEffect(() => {
    if (selectedBranchId) {
      fetchEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchId]);

  useEffect(() => {
    if (selectedClass !== "") {
      fetchDdlSection(selectedClass);
      setSelectedSection("");
      setSelectedStudent("");
      setDdlStudent([]);
    } else {
      setDdlSection([]);
      setSelectedSection("");
      setSelectedStudent("");
      setDdlStudent([]);
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedSection !== "") {
      fetchDdlStudent(selectedClass, selectedSection);
      setSelectedStudent("");
    } else {
      setDdlStudent([]);
      setSelectedStudent("");
    }
  }, [selectedSection]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

const buildRequestBody = useCallback(
    () => ({
      fromDate: fromDate,
      toDate: toDate,
      studentId: selectedStudent ? parseInt(selectedStudent) : 0,
      classId: selectedClass ? parseInt(selectedClass) : 0,
      sectionId: selectedSection ? parseInt(selectedSection) : 0,
      requestedBy: requestedBy ? parseInt(requestedBy) : 0,
      approvedBy: approvedBy ? parseInt(approvedBy) : 0,
      approvalStatus: approvalStatus,
      executionStatus: executionStatus,
    }),
    [fromDate, toDate, selectedStudent, selectedClass, selectedSection, requestedBy, approvedBy, approvalStatus, executionStatus]
  );

  const generateReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/FeeDeposit/GetFeeDiscountApprovalReport?Branch_id=${branchId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify(buildRequestBody()),
        }
      );

      if (!res.ok) {
        setSnackbarMessage("Error: Please provide proper input!");
        setOpenSnackbar(true);
        return;
      }

     const data = await res.json();

      const isEmpty =
        !data ||
        data.data === null ||
        (Array.isArray(data.data) && data.data.length === 0) ||
        (Array.isArray(data) && data.length === 0);

      if (isEmpty) {
        setSnackbarMessage(data?.msg || "No records found!");
        setOpenSnackbar(true);
        setReportData([]);
        return;
      }

      setReportData(Array.isArray(data) ? data : data.data || []);
    } catch (e) {
      setSnackbarMessage("An error occurred while fetching report data!");
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  }, [buildRequestBody, isMaster, selectedBranchId]);

  // ── Styles (reused from GenerateFee) ──
  const selectStyles = {
    width: "100%",
    borderRadius: "10px",
    background: "#ffffff",
    boxShadow: "0 2px 6px rgba(0,0,0,0.07)",
    transition: "box-shadow 0.2s ease",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#2196F3", borderWidth: "1.5px" },
    "&:hover": {
      boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#1976D2" },
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#21CBF3", borderWidth: "2px" },
    "&.Mui-disabled": { opacity: 0.55 },
    "& .MuiSelect-select": { padding: "11px 16px", fontWeight: 500, color: "#333", fontSize: "0.875rem" },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        borderRadius: "10px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        mt: 0.5,
        "& .MuiMenuItem-root": {
          fontSize: "0.875rem",
          "&:hover": { backgroundColor: "#e3f2fd" },
          "&.Mui-selected": { backgroundColor: "#e3f2fd", "&:hover": { backgroundColor: "#bbdefb" } },
        },
      },
    },
  };

  const fieldLabelSx = {
    display: "block",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#546e7a",
    mb: 0.6,
    letterSpacing: "0.03em",
  };

  return (
    <Box sx={{ padding: "24px", minHeight: "100vh" }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: "14px",
          border: "1px solid #e0e0e0",
          p: "22px 24px 20px",
          mb: 3,
          background: "#ffffff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: "18px 24px",
            mb: 2.5,
          }}
        >
          {/* Branch — Master users only */}
          {isMaster && (
            <Box>
              <Typography component="label" sx={fieldLabelSx}>Branch</Typography>
              <Select
                fullWidth
                value={selectedBranchId || ""}
                onChange={(e) => setSelectedBranchId(e.target.value)}
                displayEmpty
                sx={selectStyles}
                MenuProps={menuProps}
              >
                <MenuItem value="" disabled><em>Select Branch</em></MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.branchId} value={branch.branchId}>
                    {branch.branchName}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}

          {/* From Date */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>From Date</Typography>
            <TextField
              type="date"
              fullWidth
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": selectStyles }}
            />
          </Box>

          {/* To Date */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>To Date</Typography>
            <TextField
              type="date"
              fullWidth
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": selectStyles }}
            />
          </Box>

          {/* Class */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>Class</Typography>
            <Select
              fullWidth
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              displayEmpty
              sx={selectStyles}
              MenuProps={menuProps}
            >
              <MenuItem value=""><em>All Classes</em></MenuItem>
              {ddlClass.map((c) => (
                <MenuItem key={c.classId} value={c.classId}>{c.className}</MenuItem>
              ))}
            </Select>
          </Box>

          {/* Section */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>Section</Typography>
            <Select
              fullWidth
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              displayEmpty
              disabled={!selectedClass}
              sx={selectStyles}
              MenuProps={menuProps}
            >
              <MenuItem value=""><em>All Sections</em></MenuItem>
              {ddlSection.map((s) => (
                <MenuItem key={s.sectionId} value={s.sectionId}>{s.sectionName}</MenuItem>
              ))}
            </Select>
          </Box>

          {/* Student */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>Student</Typography>
            <Select
              fullWidth
              value={selectedStudent}
              onChange={(e) => setSelectedStudent(e.target.value)}
              displayEmpty
              disabled={!selectedSection}
              sx={selectStyles}
              MenuProps={menuProps}
            >
              <MenuItem value=""><em>All Students</em></MenuItem>
              {ddlStudent.map((s) => (
                <MenuItem key={s.studentId} value={s.studentId}>{s.studentName}</MenuItem>
              ))}
            </Select>
          </Box>

          {/* Requested By */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>Requested By</Typography>
            <Select
              fullWidth
              value={requestedBy}
              onChange={(e) => setRequestedBy(e.target.value)}
              displayEmpty
              sx={selectStyles}
              MenuProps={menuProps}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              {employees.map((emp) => (
                <MenuItem key={emp.employeeID} value={emp.employeeID}>{emp.employeeName}</MenuItem>
              ))}
            </Select>
          </Box>

          {/* Approved By */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>Approved By</Typography>
            <Select
              fullWidth
              value={approvedBy}
              onChange={(e) => setApprovedBy(e.target.value)}
              displayEmpty
              sx={selectStyles}
              MenuProps={menuProps}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              {employees.map((emp) => (
                <MenuItem key={emp.employeeID} value={emp.employeeID}>{emp.employeeName}</MenuItem>
              ))}
            </Select>
          </Box>

          {/* Approval Status — CONFIRM enum mapping with backend */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>Approval Status</Typography>
            <Select
              fullWidth
              value={approvalStatus}
              onChange={(e) => setApprovalStatus(parseInt(e.target.value))}
              sx={selectStyles}
              MenuProps={menuProps}
            >
              <MenuItem value={0}>All</MenuItem>
              <MenuItem value={1}>Pending</MenuItem>
              <MenuItem value={2}>Approved</MenuItem>
              <MenuItem value={3}>Rejected</MenuItem>
            </Select>
          </Box>

          {/* Execution Status — CONFIRM enum mapping with backend */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>Execution Status</Typography>
            <Select
              fullWidth
              value={executionStatus}
              onChange={(e) => setExecutionStatus(parseInt(e.target.value))}
              sx={selectStyles}
              MenuProps={menuProps}
            >
              <MenuItem value={0}>All</MenuItem>
              <MenuItem value={1}>Pending</MenuItem>
              <MenuItem value={2}>Executed</MenuItem>
            </Select>
          </Box>
        </Box>

        <Box sx={{ borderTop: "1px solid #f0f0f0", pt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
            <Button
              variant="contained"
              onClick={generateReport}
              disabled={isLoading}
              sx={{
                background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                borderRadius: "25px",
                padding: "9px 28px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                minWidth: "160px",
                boxShadow: "0 3px 5px 2px rgba(33,203,243,.3)",
                "&:hover": {
                  background: "linear-gradient(45deg, #1976D2 30%, #1E88E5 90%)",
                },
              }}
            >
              {isLoading ? <CircularProgress size={20} color="inherit" /> : "Generate Report"}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* ── Table area — column names are placeholders, confirm against actual API response ── */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#2196F3" }} />
        </Box>
      ) : reportData.length > 0 ? (
        <Paper
          elevation={0}
          sx={{ borderRadius: "14px", border: "1px solid #e0e0e0", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
        >
          <TableContainer sx={{ maxHeight: 500, overflow: "auto" }}>
            <Table size="small">
              <TableHead sx={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "#e3f2fd" }}>
                <TableRow sx={{ backgroundColor: "#e3f2fd" }}>
                  {[
                    "Student Name", "Adm. No", "Class", "Section",
                    "Requested By", "Approved By", "Discount Amount",
                    "Approval Status", "Execution Status", "Request Date", "Approval Date",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{ fontSize: "0.74rem", fontWeight: 700, color: "#1565c0", whiteSpace: "nowrap", py: 1.5, borderBottom: "2px solid #bbdefb" }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((item, idx) => (
                  <TableRow
                    key={item.id || idx}
                    hover
                    sx={{
                      backgroundColor: idx % 2 === 0 ? "#ffffff" : "#fafafa",
                      "&:hover": { backgroundColor: "#e3f2fd" },
                      "& td": { fontSize: "0.82rem", color: "#333", borderBottom: "1px solid #f0f0f0", py: 1.1 },
                    }}
                  >
                    <TableCell>{item.studentName || "-"}</TableCell>
                    <TableCell>{item.admissionNo || "-"}</TableCell>
                    <TableCell>{item.className || "-"}</TableCell>
                    <TableCell>{item.sectionName || "-"}</TableCell>
                    <TableCell>{item.requestedByName || "-"}</TableCell>
                    <TableCell>{item.approvedByName || "-"}</TableCell>
                    <TableCell align="right">₹{(item.discountAmount || 0).toFixed(2)}</TableCell>
                    <TableCell>{item.approvalStatusText || "-"}</TableCell>
                    <TableCell>{item.executionStatusText || "-"}</TableCell>
                    <TableCell>{item.requestDate || "-"}</TableCell>
                    <TableCell>{item.approvalDate || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Box sx={{ textAlign: "center", py: 10 }}>
          <Typography sx={{ fontSize: "2rem", mb: 1 }}>📋</Typography>
          <Typography variant="h6" sx={{ color: "#555", fontWeight: 600 }}>No Records Found</Typography>
          <Typography sx={{ fontSize: "0.875rem", color: "#999", mt: 0.5 }}>
            Try adjusting your filters to see results
          </Typography>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeeDiscountApprovalReport;